import EditOutlinedIcon from '@offline-mui/icons-material/EditOutlined'
import ScheduleIcon from '@offline-mui/icons-material/Schedule'
import { Box, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@offline-mui/material'
import { alpha } from '@offline-mui/material/styles'
import { useState } from 'react'
import { formatShortDate } from '../utils/flightFormat'
import FlightLegsEditor from './FlightLegsEditor'
import type { SelectedFlightLeg } from './FlightLegsEditor'
import {
  FlightLegRow,
  LayoverRow,
  RouteLine,
  legContainerSx,
  legGridSx,
  wallClockMinutes,
} from './ui/FlightLegRow'

export type { SelectedFlightLeg }

type SelectedFlightsCardProps = {
  legs: SelectedFlightLeg[]
  loading?: boolean
  changeDisabled?: boolean
  onChangeSchedule?: () => void
  /** When provided, an "Edit flights" action opens an inline form for every leg. */
  onSaveLegs?: (legs: SelectedFlightLeg[]) => void
}

export default function SelectedFlightsCard({
  legs,
  loading = false,
  changeDisabled = false,
  onChangeSchedule,
  onSaveLegs,
}: SelectedFlightsCardProps) {
  const [editing, setEditing] = useState(false)
  if (legs.length === 0) return null

  const first = legs[0]
  const last = legs[legs.length - 1]
  const stops = legs.length - 1
  // Leg times are airport-local, so total travel time is flying time plus
  // each layover (measured at a single airport).
  const flying = legs.reduce((sum, leg) => sum + (Number(leg.durationMin) || 0), 0)
  const waiting = legs.slice(1).reduce((sum, leg, index) => {
    const arrive = wallClockMinutes(legs[index].arrivalDatetime)
    const depart = wallClockMinutes(leg.departureDatetime)
    return arrive != null && depart != null && depart > arrive ? sum + (depart - arrive) : sum
  }, 0)
  const totalMinutes = flying > 0 ? flying + waiting : null

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: (t) => alpha(t.palette.primary.main, 0.4),
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          ...legContainerSx,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          borderBottom: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.2),
        }}
      >
      <Box sx={{ ...legGridSx(true), px: { xs: 2, md: 2.5 }, py: 2 }}>
        <Typography sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 13.5 }}>
          {formatShortDate(first.departureDatetime)}
        </Typography>
        <RouteLine
          origin={first.origin}
          destination={last.destination}
          stops={stops}
          totalMinutes={totalMinutes}
        />
        <Stack
          className="legrow-trailing"
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
        >
          {onSaveLegs ? (
            <Button
              size="small"
              variant={editing ? 'contained' : 'outlined'}
              startIcon={<EditOutlinedIcon />}
              onClick={() => setEditing((open) => !open)}
              aria-expanded={editing}
            >
              {editing ? 'Editing flights' : 'Edit flights'}
            </Button>
          ) : null}
          {onChangeSchedule ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <ScheduleIcon />}
              disabled={changeDisabled || editing}
              onClick={onChangeSchedule}
            >
              Change schedule
            </Button>
          ) : null}
        </Stack>
      </Box>
      </Box>

      {legs.map((leg, index) => {
        const previous = index > 0 ? legs[index - 1] : null
        const layover = previous
          ? (() => {
              const arrive = wallClockMinutes(previous.arrivalDatetime)
              const depart = wallClockMinutes(leg.departureDatetime)
              return arrive != null && depart != null ? depart - arrive : null
            })()
          : null
        const operatedBy =
          leg.operatingCarrier && leg.operatingCarrier !== leg.carrier
            ? leg.operatingCarrierName || leg.operatingCarrier
            : null

        return (
          <Box key={leg.key}>
            {previous ? <LayoverRow minutes={layover} airport={leg.origin} /> : null}
            <FlightLegRow
              carrier={leg.carrier}
              flightNumber={leg.flightNumber}
              aircraft={leg.aircraftType}
              origin={leg.origin}
              destination={leg.destination}
              departureDatetime={leg.departureDatetime}
              arrivalDatetime={leg.arrivalDatetime}
              durationMinutes={Number(leg.durationMin) || null}
              operatedBy={operatedBy}
              trailing={
                <>
                  <Chip size="small" color="primary" label={leg.fareBasis.cabin || 'Cabin —'} />
                  {leg.fareBasis.classOfService ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Class ${leg.fareBasis.classOfService}`}
                    />
                  ) : null}
                </>
              }
            />
          </Box>
        )
      })}

      {onSaveLegs ? (
        <Collapse in={editing} unmountOnExit>
          <FlightLegsEditor
            legs={legs}
            onCancel={() => setEditing(false)}
            onSave={(next) => {
              onSaveLegs(next)
              setEditing(false)
            }}
          />
        </Collapse>
      ) : null}
    </Box>
  )
}
